import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { NotificationPreferenceScope } from "generated-graphql/graphql";
import { useId } from "react";

import {
  AlertRow,
  PREFERENCE_GRID_COLUMNS,
  PREFERENCE_GRID_MIN_WIDTH,
  type AlertRowData,
} from "./AlertRow";
import {
  CHANNEL_CELL_SX,
  channelLabel,
  MUTED_CELL_SX,
  mutedByChannelMasterCopy,
  provenanceLabel,
  SECTION_OFF_LABEL,
} from "./preferenceProvenance";
import { PreferenceSwitch } from "./PreferenceSwitch";
import {
  countDeliveringRows,
  draftValue,
  type EmailCadenceChoice,
  isParentChannelOn,
  PREFERENCE_ROW_CHANNELS,
  type DraftParentSwitch,
  type NotificationPreferenceDraft,
} from "./useNotificationPreferenceDraft";

/**
 * One section of the preferences page: a heading with its own per-channel parent
 * switches, and the notification rows beneath them.
 *
 * The header says "Off" when nothing in the section will fire, and says nothing at all
 * otherwise — see {@link SECTION_OFF_LABEL}. That marker and the switches inside are
 * allowed to disagree: a silent section still shows every stored position, which is the
 * entire point of storing a mute as its own row rather than by overwriting the rows
 * underneath. The marker answers "does anything reach me"; the switches answer "what
 * did I choose".
 */

export type AlertSectionData = {
  groupId: string;
  label: string;
  staffOnly: boolean;
  /** One per channel, in the same order as the columns they sit in. */
  parentSwitches: readonly (DraftParentSwitch & { source: string })[];
  rows: readonly AlertRowData[];
};

export type AlertSectionAccordionProps = {
  section: AlertSectionData;
  channelParentSwitches: readonly (DraftParentSwitch & { source: string })[];
  draft: NotificationPreferenceDraft;
  scope: NotificationPreferenceScope;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** True while a write is in flight; see {@link PreferenceSwitch}. */
  disabled: boolean;
  onToggle: (kind: string, channel: string, enabled: boolean) => void;
  onEmailChoice: (kind: string, choice: EmailCadenceChoice) => void;
};

/**
 * The row of column headings.
 *
 * The channel columns are generated from {@link PREFERENCE_ROW_CHANNELS} rather
 * than written out, so a heading can never end up over the column it does not name.
 *
 * It carries no "muted from above" cue of its own. One badge per column restated
 * what the greyscale and each switch's own description already say, and at three
 * sections deep the page was mostly badges.
 */
function ColumnHeaders() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: PREFERENCE_GRID_COLUMNS,
        alignItems: "flex-end",
        columnGap: 1,
        // Tight both ways: close under the section header it belongs to, and close
        // to the rule beneath, so the labels read as part of the first line rather
        // than as their own band.
        pt: 0.5,
        pb: 0.25,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="overline" color="text.secondary">
        Notification
      </Typography>

      {PREFERENCE_ROW_CHANNELS.map((channel) => (
        <Box key={channel} sx={CHANNEL_CELL_SX}>
          <Typography variant="overline" color="text.secondary">
            {channelLabel(channel)}
          </Typography>
        </Box>
      ))}

      {/* Caret lane, empty here. */}
      <Box />
    </Box>
  );
}

/**
 * One of a section's two parent switches.
 *
 * Its own component because a section switch is both a parent and a child: it
 * silences the rows beneath it, and it is itself silenced by the page-wide switch for
 * the same channel. Both facts have to be expressed on one control, which is more
 * than fits inline in a header that also carries a title, a count and a caret.
 */
type SectionParentSwitchCellProps = {
  section: AlertSectionData;
  channel: string;
  channelParentSwitches: readonly DraftParentSwitch[];
  draft: NotificationPreferenceDraft;
  scope: NotificationPreferenceScope;
  disabled: boolean;
  onToggle: (kind: string, channel: string, enabled: boolean) => void;
  onEmailChoice: (kind: string, choice: EmailCadenceChoice) => void;
};

function SectionParentSwitchCell({
  section,
  channel,
  channelParentSwitches,
  draft,
  scope,
  disabled,
  onToggle,
  onEmailChoice,
}: SectionParentSwitchCellProps) {
  const parent = section.parentSwitches.find((entry) => entry.channel === channel);
  if (!parent) {
    return <Box sx={CHANNEL_CELL_SX} />;
  }

  // Marked exactly as its own children are when it is off: the hierarchy is only
  // legible if every level is treated the same way.
  const ownEnabled = draftValue(draft, parent.kind, parent.channel);
  const silencedFromAbove = !isParentChannelOn(draft, channelParentSwitches, channel);

  return (
    <Box sx={{ ...CHANNEL_CELL_SX, ...(silencedFromAbove ? MUTED_CELL_SX : {}) }}>
      <PreferenceSwitch
        label={`${channelLabel(channel)} for all ${section.label} notifications`}
        /*
          The mute above it when there is one — and only while this switch is itself
          on, because the promise that "these choices apply when it's turned back on"
          is not true of a switch the reader turned off. Falls back to where the
          position came from, the same thing a notification row says: an inherited
          mute has to be attributable to the organization rather than read as the
          reader's own choice.
        */
        explanation={
          ownEnabled && silencedFromAbove
            ? mutedByChannelMasterCopy(channel)
            : provenanceLabel(parent.source, scope)
        }
        checked={ownEnabled}
        disabled={disabled}
        onChange={(enabled) => onToggle(parent.kind, parent.channel, enabled)}
      />
    </Box>
  );
}

type SectionHeaderRowProps = {
  section: AlertSectionData;
  channelParentSwitches: readonly DraftParentSwitch[];
  draft: NotificationPreferenceDraft;
  scope: NotificationPreferenceScope;
  disabled: boolean;
  summaryId: string;
  detailsId: string;
  deliveringCount: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggle: (kind: string, channel: string, enabled: boolean) => void;
  onEmailChoice: (kind: string, choice: EmailCadenceChoice) => void;
};

/**
 * The section's header, which is also the notification grid's first row.
 *
 * Extracted because it is a distinct thing now that it carries its own parent
 * switches — and because the accordion around it had grown past the function-length
 * limit, which is the signal to split rather than to raise the cap.
 *
 * It stays the Accordion's FIRST child. MUI treats that child as the header and
 * exposes the expanded state through context, which is what lets `AccordionSummary`
 * live inside this component rather than directly under the accordion.
 */
function SectionHeaderRow({
  section,
  channelParentSwitches,
  draft,
  scope,
  disabled,
  summaryId,
  detailsId,
  deliveringCount,
  expanded,
  onToggleExpanded,
  onToggle,
  onEmailChoice,
}: SectionHeaderRowProps) {
  return (
    <>
      {/*
        The header is the notification grid's first row: the name and the count
        occupy the name column, and the two parent switches sit in the same two
        channel columns the notifications below use. That alignment is the whole
        point — a section switch that governs the In-app column has to live in it.

        The switches are SIBLINGS of the summary, never descendants. Nested, they
        were `<input role="switch">` elements inside the summary's `<button>` —
        invalid HTML and undefined ARIA held together only by `stopPropagation`.
      */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: PREFERENCE_GRID_COLUMNS,
          alignItems: "center",
          columnGap: 1,
          px: 3,
          // The gray band is the section divider. It reads as a heading without
          // the ruled box the outlined variant drew, which is what made the
          // sections hard to tell apart from the rows inside them.
          backgroundColor: (theme) => theme.palette.surfaceSubtle,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {/*
          The summary owns the name column and draws no caret of its own: the real
          one is a button in the last column, past the switches. Its built-in
          `expandIcon` sat at the name column's right edge, which is why the row was
          showing two.
        */}
        <AccordionSummary
          id={summaryId}
          aria-controls={detailsId}
          sx={{ minWidth: 0, px: 0, backgroundColor: "transparent" }}
        >
          {/*
            One line height across both, rather than each font's own. `overline` sets
            1.6 and `body2` 1.43, so centring two different line boxes left the count
            sitting a pixel or two off the title's optical centre — visible as soon as
            you look down a column of section headers.
          */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              color="text.primary"
              sx={{ fontSize: "0.8125rem", fontWeight: 700, lineHeight: 1.5 }}
            >
              {section.label}
            </Typography>
            {deliveringCount === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {SECTION_OFF_LABEL}
              </Typography>
            )}
            {section.staffOnly && (
              // The only warning-coloured thing on the page. It renders solely
              // for staff callers — a customer is never offered this section —
              // and exists so an Encamp employee can see at a glance which
              // switches affect nobody but Encamp.
              <Chip label="Encamp only" size="small" variant="outlined" color="warning" />
            )}
          </Stack>
        </AccordionSummary>

        {PREFERENCE_ROW_CHANNELS.map((channel) => (
          <SectionParentSwitchCell
            key={channel}
            section={section}
            channel={channel}
            channelParentSwitches={channelParentSwitches}
            draft={draft}
            scope={scope}
            disabled={disabled}
            onToggle={onToggle}
            onEmailChoice={onEmailChoice}
          />
        ))}

        {/*
          The caret sits to the right of the switches, in its own column. It is a
          real button rather than the summary's built-in `expandIcon`, because that
          icon renders inside the summary — and a summary wide enough to reach past
          the switches would have to contain them, which is the nested-interactive
          markup this layout exists to avoid.
        */}
        <Box sx={CHANNEL_CELL_SX}>
          <IconButton
            size="small"
            aria-label={`${expanded ? "Collapse" : "Expand"} ${section.label}`}
            aria-expanded={expanded}
            aria-controls={detailsId}
            onClick={() => onToggleExpanded()}
          >
            <ExpandMore
              sx={{
                transform: expanded ? "rotate(180deg)" : "none",
                transition: (theme) => theme.transitions.create("transform"),
              }}
            />
          </IconButton>
        </Box>
      </Box>
    </>
  );
}

export function AlertSectionAccordion({
  section,
  channelParentSwitches,
  draft,
  scope,
  expanded,
  onToggleExpanded,
  disabled,
  onToggle,
  onEmailChoice,
}: AlertSectionAccordionProps) {
  const deliveringCount = countDeliveringRows(section, draft, channelParentSwitches);
  const summaryId = useId();
  const detailsId = useId();

  return (
    /*
      A band inside the Alerts widget rather than a widget of its own. Five
      floating papers said nothing about what they collectively were, which is why
      it was hard to tell which section a given row belonged to.
    */
    /*
      Space below an expanded section rather than above every one. Collapsed
      sections read as a list and want to sit close together; an open one needs its
      rows separated from the next header.
    */
    <Box sx={{ mb: expanded ? 3 : 0 }}>
      <Accordion
        /*
        No outline and no shadow: the Alerts widget is the container, so an
        outlined accordion inside it drew a second box around the same content.
        What separates one section from the next is the gray header band below,
        which is quieter and matches how the rest of the app groups rows.
      */
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={onToggleExpanded}
        sx={{ "&::before": { display: "none" }, backgroundColor: "transparent" }}
        /*
        MUI wires the collapsible region from the *first child's* props, which is
        the header row below rather than the summary button. Naming the region from
        the button instead keeps its accessible name to "<section>, N of M on"
        rather than folding in the master switch that sits alongside.
      */
        slotProps={{ region: { id: detailsId, "aria-labelledby": summaryId } }}
      >
        <SectionHeaderRow
          section={section}
          channelParentSwitches={channelParentSwitches}
          draft={draft}
          scope={scope}
          disabled={disabled}
          summaryId={summaryId}
          detailsId={detailsId}
          deliveringCount={deliveringCount}
          expanded={expanded}
          onToggleExpanded={onToggleExpanded}
          onToggle={onToggle}
          onEmailChoice={onEmailChoice}
        />
        <AccordionDetails sx={{ px: 3, pt: 0.5, pb: 1 }}>
          {/*
          The grid holds `PREFERENCE_GRID_MIN_WIDTH` of columns at their floor —
          restated as a reference rather than a number, because the last hard-coded
          figure went stale the moment a column gained a minimum. A narrow window
          scrolls it rather than crushing the alert names into a column of single
          characters. The scroll lives here rather than on the page so the Delivery
          card and the section headers stay put while the grid moves.
        */}
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: PREFERENCE_GRID_MIN_WIDTH }}>
              <ColumnHeaders />
              {/*
              The section mute marks each row's toggle columns rather than the whole
              block: the titles and descriptions stay legible, because a reader
              deciding whether to un-mute the section has to be able to read what is
              in it. `AlertRow` applies that per channel, and nothing wraps it in a
              second layer — two nested treatments would compound into unreadable.
            */}
              {section.rows.map((row) => (
                <AlertRow
                  key={row.kind}
                  row={row}
                  sectionParentSwitches={section.parentSwitches}
                  channelParentSwitches={channelParentSwitches}
                  draft={draft}
                  scope={scope}
                  disabled={disabled}
                  onToggle={onToggle}
                  onEmailChoice={onEmailChoice}
                />
              ))}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
